import React, { useState, useEffect } from 'react'
import TopDestinationsLinks from './TopDestinationsLinks'
import { generateNearbyCitiesPropsFromLocations } from '../../domain/useCases/generateNearbyCitiesPropsFromLocations'
import { replaceAllSpacesWithDashes } from '../../utils/replaceAllSpaces'
import { findNearestLocations } from '../../utils/findNearestLocations'
import Section from '@/components/Section'
import { isLocationCityOrRegion } from '../../domain/location/useCases/isLocationCityOrRegion'
import { isNonEmptyArray, isNonEmptyString } from '../../utils/checks'
import { isLocationInUnitedStates } from '../../domain/location/useCases/isLocationInUnitedStates'
import { getAdOpsCookie } from '../../domain/useCases/adminBarInfo/adminBarUtils'
import { Location, LocationAllowList, PageType } from '../../types/models'
import LinkButton from '../atoms/LinkButton/LinkButton'
import { ArrowRight } from 'react-bootstrap-icons'

const RENDER_DESTINATION_LINKS_THRESHOLD = 2
const TOP_DESTINATION_LINKS_COLUMN_LIMIT = 5
const MAXIMUM_LINKS_TO_SHOW = 15

export default function NearbyCities({
  location,
  locationAllowList,
  pageType
}: {
  location: Location
  locationAllowList: LocationAllowList
  pageType: PageType
}): JSX.Element | null {
  const isCityOrRegion = isLocationCityOrRegion(location)
  const isUSLocation = isLocationInUnitedStates(location)
  const [showAdOpsInfo, setShowAdOpsInfo] = useState(false)
  const [showDebug, setShowDebug] = useState(false)
  useEffect(() => {
    if (getAdOpsCookie()) {
      setShowDebug(true)
    }
  }, [])

  const nearestLocations = findNearestLocations({
    referenceLocation: location,
    locationsData: locationAllowList,
    isUSLocation
  })

  const locationEdges = nearestLocations
    .slice(0, MAXIMUM_LINKS_TO_SHOW)
    .map((city) => {
      return {
        ...city,
        linkTitle: city.title.concat(', ', city.parentLocation)
      }
    })

  const propsForNearbyCities = generateNearbyCitiesPropsFromLocations({
    allLocations: locationEdges
  })

  const shouldRenderNearbyCities =
    isNonEmptyArray(propsForNearbyCities) &&
    propsForNearbyCities.length >= RENDER_DESTINATION_LINKS_THRESHOLD

  if (
    !isCityOrRegion ||
    !shouldRenderNearbyCities ||
    !isNonEmptyString(location?.parentLocation)
  ) {
    return null
  }

  return (
    <div className="relative z-10 -mx-4 flex flex-col flex-nowrap overflow-hidden bg-stone-100 md:mx-0 md:flex-wrap lg:rounded-[20px]">
      <Section>
        {showDebug && (
          <div className="absolute flex flex-row gap-x-4">
            <button
              onClick={() => setShowAdOpsInfo(!showAdOpsInfo)}
              className="z-40 rounded-md"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                className={
                  showAdOpsInfo
                    ? 'size-6 bg-secondary-400 fill-gray-50 stroke-gray-50'
                    : `size-6 fill-secondary-400 stroke-secondary-500`
                }
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 0 1-1.125-1.125v-3.75ZM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-8.25ZM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 0 1-1.125-1.125v-2.25Z"
                />
              </svg>
            </button>
            {showAdOpsInfo && (
              <span className="mt-2 w-full">
                <ul
                  className={`absolute z-40 block max-h-[300px] gap-1 overflow-y-auto bg-primary-50 p-3 text-xs text-gray-700 shadow-md`}
                >
                  {nearestLocations.map((location, i) => {
                    return (
                      <li
                        key={location.location_id}
                        className="inline-flex flex-row gap-x-1 whitespace-nowrap"
                      >
                        <span>{i + 1}</span> <span>{location.title}</span>
                        <span className="text-gray-500">
                          ( {location.center_count} ) -
                        </span>
                        <span className="text-gray-500">
                          {location.distance.toFixed(2)} km
                        </span>
                        <span className="text-gray-500">
                          {location.location_id}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </span>
            )}
          </div>
        )}
        <div className="mx-4 flex max-w-screen-xl flex-col items-start self-stretch bg-stone-100 py-8 md:mx-0 md:flex-row md:flex-wrap md:px-6 md:py-10 lg:px-8">
          <div className="mb-4 md:mb-8">
            <div className="w-[324px] flex-1 break-words">
              <div className="pb-2 text-left font-FieldsVariable text-3xl font-medium text-gray-1100">
                Nearby Rehabs
              </div>

              {isCityOrRegion && (
                <LinkButton
                  label={`All ${location?.parentLocation} Centers`}
                  href={`/${replaceAllSpacesWithDashes(
                    location?.parentLocation?.toLowerCase()
                  )}/`}
                  size="large"
                  underline="always"
                  iconElement={
                    <ArrowRight
                      size="16"
                      className="ml-1"
                    />
                  }
                />
              )}
            </div>
          </div>
          <div className="flex grow flex-col gap-x-6 md:flex-row md:flex-nowrap">
            {propsForNearbyCities
              .slice(0, TOP_DESTINATION_LINKS_COLUMN_LIMIT)
              .map((topDestinationProps, i) => {
                return (
                  <TopDestinationsLinks
                    {...topDestinationProps}
                    key={topDestinationProps[0].slug}
                  />
                )
              })}
          </div>
        </div>
      </Section>
    </div>
  )
}
